import type { Config } from 'app/config';
import { MIRROR_REQUEST_TIMEOUT_MS, withTimeout } from 'app/utils/hyrox-mirror/graphql';
import fetch, { RequestError } from 'app/utils/requests/fetch';
import urlQueryParams from 'app/utils/requests/query-params';
import { extractUserIdFromToken } from 'state/auth';
import logger from 'utils/logger';

const ACCESS_TOKEN_KEY = 'hyrox_mirror_access_token';
const REFRESH_TOKEN_KEY = 'hyrox_mirror_refresh_token';
const EXPIRES_AT_KEY = 'hyrox_mirror_expires_at';
const SESSION_ID_KEY = 'hyrox_mirror_session_id';
const REFRESH_AHEAD_MS = 60_000;

export type MirrorSessionFailure = 'mirror_session_unavailable' | 'mirror_session_expired';

export type MirrorAccess = { ok: true; accessToken: string } | { ok: false; reason: MirrorSessionFailure };

export interface MirrorConfig {
  baseUrl: string;
}

export interface MirrorSession {
  getAccessToken: () => Promise<MirrorAccess>;
  getSessionId: () => string | null;
  getUserId: () => number | null;
  mint: (cognitoToken: string) => Promise<void>;
  refreshAccessToken: () => Promise<string | null>;
}

interface HyroxTokenResponse {
  accessToken: string;
  accessTokenExpiresAt?: string;
  refreshToken?: string;
  refreshTokenExpiresAt?: string;
}

const hostOf = (url: string | undefined): string | null => {
  if (!url) {
    return null;
  }
  try {
    return new URL(url).host;
  } catch {
    return null;
  }
};

// A dead grant comes back as 400 (oauth2-server's invalid_grant / invalid_client) or 401; anything else
// (5xx during a deploy, a proxy hiccup) says nothing about the session
const isRejectedGrant = (error: RequestError): boolean => error.status === 400 || error.status === 401;

// Off unless the var is set AND points somewhere other than the primary API: a rollback build that flips
// the primary back to hyrox-api but forgets to unset the mirror var would otherwise write hyrox twice.
export const resolveMirrorConfig = (config: Config): MirrorConfig | null => {
  const baseUrl = config.REACT_APP_HYROX_MIRROR_BASE_URL;
  const mirrorHost = hostOf(baseUrl);

  if (!baseUrl || mirrorHost === null || mirrorHost === hostOf(config.REACT_APP_HYROX_API_URL)) {
    return null;
  }

  return { baseUrl };
};

// Kept apart from state/auth on purpose: hyrox's 1h expiry must never sign the primary session out.
// The keys live in localStorage, which state/auth's clearState() wipes wholesale on sign-out and expiry.
//
// No client credentials anywhere: hyrox-app is a public client after the F-track flip, so both hyrox
// endpoints this talks to (/cognito/exchange, /cognito/refresh) authenticate the browser by what it
// already holds — the Cognito id token, then the refresh token — and hyrox-api supplies the hyrox_app
// credential server-side.
export const createMirrorSession = ({ baseUrl }: MirrorConfig): MirrorSession => {
  const state: { refreshing: Promise<string | null> | null; minting: Promise<void> | null } = {
    refreshing: null,
    minting: null,
  };

  const persist = ({ accessToken, accessTokenExpiresAt, refreshToken }: HyroxTokenResponse): void => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);

    if (accessTokenExpiresAt) {
      localStorage.setItem(EXPIRES_AT_KEY, accessTokenExpiresAt);
    } else {
      localStorage.removeItem(EXPIRES_AT_KEY);
    }

    // Refresh tokens are one-time-use on hyrox-api, so whatever we held before is spent either way
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    } else {
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
  };

  const clear = (): void => {
    for (const key of [ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY, EXPIRES_AT_KEY, SESSION_ID_KEY]) {
      localStorage.removeItem(key);
    }
  };

  // Bounded like every other mirror request: an un-timed exchange hanging at login would leave
  // getAccessToken() awaiting state.minting forever, and that login would mirror nothing, silently
  const postToken = (path: string, params: Record<string, string>): Promise<HyroxTokenResponse> =>
    withTimeout(MIRROR_REQUEST_TIMEOUT_MS, async (signal) => {
      const { body } = await fetch(`${baseUrl}${path}`, {
        body: urlQueryParams(params),
        headers: {
          'content-type': 'application/x-www-form-urlencoded',
        },
        method: 'POST',
        signal,
      });

      return body as HyroxTokenResponse;
    });

  // Which login the stored tokens belong to. Callers that capture work for later (the replay queue) hold on
  // to it and drop the work when it no longer matches, so a write can never be attributed to another user.
  const getSessionId = (): string | null => localStorage.getItem(SESSION_ID_KEY);

  // Never rejects: a mirror failure must not need handling at the login call site, and the reporting
  // vocabulary belongs here rather than duplicated into a page. A 401 is the expected "user not on hyrox"
  // baseline, a 5xx is hyrox being down — the status is reported, never the token or the response body.
  const mint = (cognitoToken: string): Promise<void> => {
    // Whatever was minted for a previous login must not outlive it: a failed exchange for this user
    // would otherwise leave the previous user's session live and attribute their writes to it
    clear();

    // Written before the exchange, not after it: mutations fired on the home page while the exchange is
    // still in flight belong to this login, and have to be able to recognise it as theirs
    const sessionId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(SESSION_ID_KEY, sessionId);

    const minting = postToken('/cognito/exchange', { token: cognitoToken })
      .then((response) => {
        // Only the mint that still owns the session id may persist. Two overlapping exchanges settling out
        // of order would otherwise leave the older user's tokens installed under the newer login's session
        // id: every replay captured for the newer login would pass the sameLogin() check and then be sent
        // with the older user's hyrox identity, which is the one thing the account boundary exists to stop.
        if (getSessionId() !== sessionId) {
          return;
        }

        persist(response);
      })
      .catch((error: unknown) =>
        logger.warn({
          message: 'hyrox-mirror: mirror_session_unavailable',
          context: {
            kind: 'mirror_session_unavailable',
            reason: error instanceof RequestError ? `exchange_${error.status}` : 'exchange_network',
          },
        }),
      )
      .finally(() => {
        // Only the latest mint owns the slot: an older one settling must not unhook a newer in-flight one
        if (state.minting === minting) {
          state.minting = null;
        }
      });

    state.minting = minting;

    return minting;
  };

  const refreshAccessToken = (): Promise<string | null> => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

    if (!refreshToken) {
      return Promise.resolve(null);
    }

    const sessionId = getSessionId();

    if (state.refreshing === null) {
      // /cognito/refresh, not /oauth2/token: the refresh_token grant with hyrox_app's credential applied
      // server-side, so no client secret is bundled into the app or put on the wire
      state.refreshing = postToken('/cognito/refresh', { refresh_token: refreshToken })
        .then((response) => {
          // Same ownership rule as mint(): a login that landed while this was in flight owns the session
          // now, and these tokens belong to the previous one
          if (getSessionId() !== sessionId) {
            return null;
          }

          persist(response);
          return response.accessToken;
        })
        .catch((error: unknown) => {
          // Another tab spent this one-time-use token first and rotated the session: its tokens are the live ones
          if (localStorage.getItem(REFRESH_TOKEN_KEY) !== refreshToken) {
            return localStorage.getItem(ACCESS_TOKEN_KEY);
          }

          if (error instanceof RequestError && isRejectedGrant(error)) {
            clear();
          }

          return null;
        })
        .finally(() => {
          state.refreshing = null;
        });
    }

    return state.refreshing;
  };

  const getAccessToken = async (): Promise<MirrorAccess> => {
    // The home page fires its first mutation right after SSO login, while the exchange may still be in flight
    await state.minting?.catch(() => undefined);

    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);

    if (!accessToken) {
      return { ok: false, reason: 'mirror_session_unavailable' };
    }

    // Tokens with no login binding are not usable. Two tabs refreshing at once can interleave one tab's
    // clear() — which drops the session id — with the other's persist(), which only ever writes tokens;
    // that leaves live tokens whose owning login is unrecorded. A replay bound to a real login already
    // drops itself in that state, and this stops an unbound one from being sent instead of dropped.
    if (getSessionId() === null) {
      return { ok: false, reason: 'mirror_session_unavailable' };
    }

    const expiresAt = localStorage.getItem(EXPIRES_AT_KEY);
    const isExpiring = expiresAt !== null && new Date(expiresAt).getTime() - Date.now() < REFRESH_AHEAD_MS;

    if (!isExpiring) {
      return { ok: true, accessToken };
    }

    const refreshed = await refreshAccessToken();

    return refreshed ? { ok: true, accessToken: refreshed } : { ok: false, reason: 'mirror_session_expired' };
  };

  const getUserId = (): number | null => {
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);

    if (!accessToken) {
      return null;
    }

    try {
      const userId = extractUserIdFromToken(accessToken);
      return Number.isNaN(userId) ? null : userId;
    } catch {
      return null;
    }
  };

  // clear() is deliberately not exported: sign-out already wipes the hyrox_mirror_* keys, because
  // state/auth's clearState() calls localStorage.clear() wholesale.
  return {
    getAccessToken,
    getSessionId,
    getUserId,
    mint,
    refreshAccessToken,
  };
};
